/**
 * ALDER SEARCH Team Section
 * Design: "Alder Forest Organic Precision"
 * Forest green background with single Founding Partner card
 * Real photo of Ryan Ollerenshaw
 */

import { useEffect, useRef } from "react";
import { media } from "@/data/media";

export default function TeamSection() {
  const cardRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const allEls = [headerRef.current, cardRef.current];
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    allEls.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      el.style.transition = `opacity 0.7s ease-out ${i * 150}ms, transform 0.7s ease-out ${i * 150}ms`;
      observer.observe(el);
    });
    const fallback = setTimeout(() => {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      });
    }, 2000);
    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <section
      id="team"
      className="py-28 relative overflow-hidden"
    >
      {/* Background image */}
      <picture className="absolute inset-0 block">
        <source type="image/avif" srcSet={media.team.avif} />
        <img
          src={media.team.webp}
          srcSet={media.team.srcSet}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          width={media.team.width}
          height={media.team.height}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover object-center"
        />
      </picture>
      {/* Dark overlay */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(27, 58, 45, 0.93)" }}
      />

      <div className="container relative z-10">
        {/* Section Header */}
        <div ref={headerRef} className="mb-14">
          <div className="flex items-center gap-3 mb-5">
            <div
              className="h-px w-10"
              style={{ background: "oklch(0.62 0.085 75)" }}
            />
            <span
              className="font-ui text-xs tracking-widest uppercase"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.18em",
              }}
            >
              Founding Partner
            </span>
          </div>
          <h2
            className="font-display"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(2rem, 4vw, 3.2rem)",
              color: "oklch(0.952 0.012 80)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
            }}
          >
            The Person Behind
            <br />
            <em style={{ color: "oklch(0.72 0.09 75)", fontStyle: "italic" }}>
              Alder Search
            </em>
          </h2>
        </div>

        {/* Founder Card */}
        <div
          ref={cardRef}
          className="grid grid-cols-1 lg:grid-cols-3 gap-0 overflow-hidden"
          style={{
            border: "1px solid oklch(0.38 0.07 155)",
            borderRadius: "4px",
          }}
        >
          {/* Photo column */}
          <div
            className="relative overflow-hidden"
            style={{ minHeight: "380px" }}
          >
            <picture>
              <source type="image/avif" srcSet={media.ryan.avif} />
              <img
                src={media.ryan.webp}
                srcSet={media.ryan.srcSet}
                sizes="(min-width: 1024px) 400px, 100vw"
                alt="Ryan Ollerenshaw Founding Partner, Alder Search"
                width={media.ryan.width}
                height={media.ryan.height}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover object-center"
                style={{ minHeight: "380px" }}
              />
            </picture>
            {/* Subtle gradient at bottom */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to right, transparent 60%, oklch(0.245 0.065 155) 100%)",
              }}
            />
          </div>

          {/* Content column */}
          <div
            className="lg:col-span-2 p-10 lg:p-12 flex flex-col justify-center"
            style={{ background: "oklch(0.245 0.065 155)" }}
          >
            <h3
              className="font-display mb-1"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "1.8rem",
                color: "oklch(0.952 0.012 80)",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}
            >
              Ryan Ollerenshaw
            </h3>
            <div
              className="font-ui mb-1"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 500,
                fontSize: "0.82rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.05em",
              }}
            >
              Founding Partner
            </div>
            <div
              className="font-ui mb-7"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontWeight: 300,
                fontSize: "0.65rem",
                color: "oklch(0.52 0.02 155)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Product Executive Search for VC &amp; PE
            </div>

            <p
              className="font-body mb-5"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 300,
                fontSize: "0.95rem",
                lineHeight: 1.85,
                color: "oklch(0.78 0.015 80)",
                maxWidth: "560px",
              }}
            >
              Alder is named for a tree known for one thing above all else: its roots hold
              ground that would otherwise give way. Ryan has spent 16 years applying that
              same principle to product leadership recruitment for VC and PE — placing
              the product leaders who stabilise, scale, and deliver returns for
              investors and their portfolio companies.
            </p>
            <p
              className="font-body mb-8"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 300,
                fontSize: "0.95rem",
                lineHeight: 1.85,
                color: "oklch(0.78 0.015 80)",
                maxWidth: "560px",
              }}
            >
              Across hundreds of mandates spanning CPO, Head of Product, VP Product, and
              Principal Product Manager searches, he has become embedded in the VC and PE product
              ecosystem — not as an advisor, but as someone who understands the pace, the pressure,
              and the precision these environments demand.
            </p>

            {/* LinkedIn link */}
            <a
              href="https://www.linkedin.com/in/ryanollerenshaw/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-ui text-sm transition-all duration-200 self-start"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 500,
                fontSize: "0.78rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.04em",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.82 0.1 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              Connect on LinkedIn
            </a>

            {/* Book a meeting link */}
            <a
              href="https://calendar.app.google/d2FJuTHLssHHS3Lo9"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-ui text-sm transition-all duration-200 self-start mt-3"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 500,
                fontSize: "0.78rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.04em",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.82 0.1 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Book a call
            </a>
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className="mt-10 p-8 flex flex-col md:flex-row items-center justify-between gap-6"
          style={{
            background: "oklch(0.32 0.07 155)",
            borderRadius: "4px",
            border: "1px solid oklch(0.38 0.07 155)",
          }}
        >
          <div>
            <h3
              className="font-display mb-1"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 600,
                fontSize: "1.3rem",
                color: "oklch(0.952 0.012 80)",
              }}
            >
              Ready to discuss a mandate?
            </h3>
            <p
              className="font-body"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 300,
                fontSize: "0.9rem",
                color: "oklch(0.72 0.02 155)",
              }}
            >
              We are available for confidential conversations about your
              leadership needs.
            </p>
          </div>
          <button
            onClick={() => {
              const el = document.querySelector("#contact");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="flex-shrink-0 font-ui px-8 py-3 text-sm transition-all duration-300"
            style={{
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              background: "oklch(0.62 0.085 75)",
              color: "oklch(0.18 0.055 155)",
              borderRadius: "2px",
              border: "none",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)";
            }}
          >
            Engage Us
          </button>
        </div>
      </div>
    </section>
  );
}
